/**
 * MARKET CONDITIONS — one gloss, one author, shared by server and client.
 *
 * Measured on the live MARKET SNAPSHOT card, 2026-09-20. One quantity — the
 * regime — was written by THREE authors and appeared twice on the same card in
 * two different spellings, neither of them this dictionary's:
 *
 *   badge   "WATCH . Neutral / range-bound"   <- MarketSnapshotSurface.formatRegime
 *   prose   "Market regime: neutral_range_bound."  <- server/routes.ts composer
 *   nobody  "Prices are moving sideways"      <- publicRegimeLabel, the reviewed gloss
 *
 * `formatRegime` did precisely what `publicTerminology.ts`'s own header bans:
 * "it is never title-cased or underscore-stripped into something that merely
 * looks human." A de-underscored enum is a RESTATEMENT, not a gloss - the
 * reader still has to know what "range-bound" means, which is the whole thing
 * a retail companion is supposed to remove.
 *
 * The prose is composed on the SERVER, so the dictionary has to live in
 * `shared/` for the server to reach it. That is the actual fix: not three
 * call sites agreeing to behave, but one dictionary they all read.
 *
 * Fails closed. An unknown key is never underscore-stripped into fake English.
 */
const REGIME_LABELS: Readonly<Record<string, string>> = {
  bullish: "Buying has the advantage",
  bearish: "Selling has the advantage",
  neutral: "Buying and selling are balanced",
  range_bound: "Prices are moving sideways",
  neutral_range_bound: "Prices are moving sideways",
  high_volatility: "Price moves are unusually large",
  low_volatility: "Price moves are relatively small",
  accumulation: "Buying is building gradually",
  distribution: "Selling is building gradually",
  bull_to_bear: "Buying turned into selling",
  bear_to_bull: "Selling turned into buying",
};

/** The unknown-value phrase. Named so a caller cannot accidentally assert on it. */
export const REGIME_UNKNOWN = "Market conditions are still being assessed";

/** Normalise `RegimeType.NeutralRangeBound`, `neutral-range-bound`, etc. to one key. */
export function regimeKey(value: unknown): string {
  return String(value ?? "")
    .trim()
    .replace(/^\w+Type\./, "")
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/[\s-]+/g, "_")
    .toLowerCase();
}

/** The reader's sentence for a market regime, or the honest unknown. */
export function publicRegimeLabel(value: unknown): string {
  return REGIME_LABELS[regimeKey(value)] ?? REGIME_UNKNOWN;
}

/** True when this regime has a reviewed gloss - so a caller can withhold rather than guess. */
export function hasRegimeLabel(value: unknown): boolean {
  return regimeKey(value) in REGIME_LABELS;
}
